import React, { useContext, useEffect, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useParams } from 'react-router-dom';
import sessionsService from '../../services/sessionsServices';
import { AuthContext } from '../../Context/authContext';
import { useContextHook } from 'use-context-hook';
import SourcesTab from './Sources';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import 'swiper/css';
import 'swiper/css/navigation';
import { ChatSectionStyled, ChatColumn, SearchResultCol } from './ChatSection.styles';
import Markdown from 'react-markdown';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { Bot } from 'lucide-react';
import KnowHowChatHeader from './KnowHowChatHeader';
import KnowHowChatInput from './KnowHowChatInput';
import { ChatEmptyState } from '../ui/ui.styles';
import { DEFAULT_AGENT_ID } from '../../constants/defaultAgent';

const ChatSection = () => {
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const [activeTab, setActiveTab] = useState('Sources');
  const {
    selectedAgent,
    chatMessages,
    chatLoaderMessages,
    setChatmessages,
    setSessionAgent,
    sessionAgent,
    isActiveRef,
    setSessionID,
    updateAgent,
  } = useContext(TemporaryChatContext);
  const [selectedChatSources, setSelectedChatSources] = useState(null);
  const { id } = useParams();
  const tabs = ['Sources'];
  const hasSession = Boolean(id);
  const hasMessages = Boolean(chatMessages?.length);

  const getSessions = async sessionId => {
    try {
      setChatmessages([]);
      const aiResp = await sessionsService.getSessionHistory({
        user_id: user?.data?.ID,
        platform_id: 'knowhowlabs',
        session_id: sessionId,
      });

      if (aiResp?.agent_name || aiResp?.agent_id) {
        const agentObj = {
          label: aiResp?.agent_name || selectedAgent?.label || 'Agent',
          value: aiResp?.agent_id || sessionAgent?.value || selectedAgent?.value || DEFAULT_AGENT_ID,
          icon: aiResp?.icon || sessionAgent?.icon || selectedAgent?.icon || null,
          mode: aiResp?.mode || 'agent',
        };
        setSessionAgent(agentObj);
        updateAgent(agentObj);
      }

      const formattedMessages = aiResp?.history?.flatMap(item => {
        const isSource = item?.sources ? item?.sources : [];
        const websearchSources = isSource?.filter(src => src.type === 'google_search');
        const fileSources = isSource?.filter(src => src.type === 'file');
        return [
          {
            human: item.human,
            ai: item?.ai,
            source: websearchSources.length > 0 ? websearchSources : [],
            files: fileSources.length > 0 ? fileSources : [],
            images: item?.images,
            isLoading: false,
          },
        ];
      });

      setChatmessages(formattedMessages);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id && isActiveRef.current) {
      getSessions(id);
      setSessionID(id);
    }
    if (!id) {
      setChatmessages([]);
      setSessionID(null);
    }
  }, [id]);

  function getIconSrc(agent, mimeType = 'image/png') {
    const icon = agent?.icon;
    if (!icon || typeof icon !== 'string') return null;
    if (icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('data:') || icon.startsWith('/')) {
      return icon;
    }
    return `data:${mimeType};base64,${icon}`;
  }

  const emptyAgent = id && sessionAgent?.value ? sessionAgent : selectedAgent?.label ? selectedAgent : sessionAgent;
  const emptyIconSrc = getIconSrc(emptyAgent) || getIconSrc(sessionAgent) || getIconSrc(selectedAgent);
  const emptyTitle = emptyAgent?.label || 'Noel KnowHow AIA Copilot';
  const messageIconSrc = getIconSrc(sessionAgent) || getIconSrc(selectedAgent);
  const lastMessageIndex = (chatMessages?.length || 0) - 1;

  return (
    <ChatSectionStyled className={selectedChatSources !== null ? 'sources-active' : ''}>
      <ChatColumn className="poly-chat-layout">
        <KnowHowChatHeader />

        <div className="text-block poly-chat-messages">
          {!hasMessages ? (
            <ChatEmptyState className="poly-chat-empty">
              <div className="icon">
                {emptyIconSrc ? <img src={emptyIconSrc} alt="" /> : <Bot size={32} />}
              </div>
              <h3>{emptyTitle}</h3>
              <p>
                {hasSession
                  ? 'Your conversational interface with Noel KnowHow AIA Copilot. Send a message to begin.'
                  : emptyTitle
                    ? `Click New Chat to start a session with ${emptyTitle}.`
                    : 'Select an agent, then click New Chat to create a session before messaging.'}
              </p>
            </ChatEmptyState>
          ) : (
            <div className="poly-chat-thread">
              {chatMessages?.map((elem, ind) => (
                <div key={`${id}-${ind}-${elem.human?.slice(0, 20) ?? ''}`}>
                  {elem.human && (
                    <div className="heading user-text">
                      <div className="icon-box">{user?.first_name?.split('')[0]}</div>
                      <strong className="title whitespace-pre-line">{elem?.human}</strong>
                      {(elem?.source?.length > 0 || elem?.files?.length > 0) && (
                        <button
                          className="see-source-button"
                          type="button"
                          style={{
                            border: ind === selectedChatSources ? `2px solid var(--primary)` : undefined,
                          }}
                          onClick={() => setSelectedChatSources(ind)}>
                          see sources
                        </button>
                      )}
                    </div>
                  )}

                  <div className="heading">
                    <div className="icon-box icon-box--purple">
                      {messageIconSrc ? <img src={messageIconSrc} alt="" /> : <Bot size={20} />}
                    </div>

                    {elem?.images && elem?.images?.length > 0 && elem?.images !== '' && elem?.isLoading === false && (
                      <div className="swiper-holder">
                        <Swiper
                          slidesPerView={4.2}
                          navigation={{
                            prevEl: '.swiper-button-prev',
                            nextEl: '.swiper-button-next',
                          }}
                          modules={[Navigation]}
                          breakpoints={{
                            320: { slidesPerView: 1.3 },
                            574: { slidesPerView: 2.2 },
                            1250: { slidesPerView: 2.8 },
                            1440: { slidesPerView: 3.2 },
                            1750: { slidesPerView: 4.2 },
                          }}>
                          {elem?.images?.map((data, index) => (
                            <SwiperSlide key={index}>
                              <a href={data?.origin_url} target="_blank" rel="noreferrer" className="img-wrap">
                                <img src={data?.image_url} alt="img" />
                              </a>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                        <div className="swiper-controls">
                          <button type="button" className="swiper-button-prev">
                            <IoIosArrowBack />
                          </button>
                          <button type="button" className="swiper-button-next">
                            <IoIosArrowForward />
                          </button>
                        </div>
                      </div>
                    )}

                    {elem.ai && elem.ai !== '' && elem?.isLoading === false && (
                      <div className="ai-message-content">
                        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                          {elem?.ai}
                        </Markdown>
                      </div>
                    )}

                    {elem?.isLoading && (
                      <>
                        {chatLoaderMessages?.newChunk?.tool_name ? (
                          <strong className="chat-loader-text">{chatLoaderMessages?.newChunk?.tool_name}...</strong>
                        ) : (
                          <div style={{ width: '100%' }}>
                            <strong className="chat-loader-text">Thinking...</strong>
                          </div>
                        )}
                      </>
                    )}

                    {ind === lastMessageIndex && chatLoaderMessages?.newChunk?.tool_name && (
                      <strong className="chat-loader-text">{chatLoaderMessages?.newChunk?.tool_name}...</strong>
                    )}

                    {!elem.ai &&
                      !elem?.isLoading &&
                      (user?.usage?.api_calls >= user?.plan?.request_limit ? (
                        <span className="error-text">❌ Limit exceeded. Please upgrade your plan.</span>
                      ) : (
                        <span className="error-text">Something went wrong !!!</span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="poly-chat-input">
          <div className="poly-chat-search">
            <KnowHowChatInput sessionId={id} />
          </div>
        </div>
      </ChatColumn>

      {selectedChatSources !== null && (
        <SearchResultCol>
          <div>
            <ul className="tablist">
              {tabs.map((tab, index) => (
                <li key={tab}>
                  <button type="button" className={tab === activeTab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
                    {tab}
                  </button>
                </li>
              ))}
              <button type="button" className="source-close" title="close" onClick={() => setSelectedChatSources(null)}>
                <RxCross2 color="var(--danger)" />
              </button>
            </ul>
            <div className="tab-content">
              {activeTab === 'Sources' && <SourcesTab data={chatMessages[selectedChatSources]} />}
            </div>
          </div>
        </SearchResultCol>
      )}
    </ChatSectionStyled>
  );
};

export default ChatSection;
